import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { CircularProgress, Slider, SliderRail, styled, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { SliderThumb } from "@mui/material/Slider";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingState } from "../../../model/LoadingState";

const SwipeButtonSlider = styled(Slider)(({ value, disabled }) => ({
    height: 64,
    color: "#F5F5F5",
    width: "100%",
    userSelect: "none",
    pointerEvents: "none",
    "& .MuiSlider-rail": {
        borderRadius: 32,
        opacity: 1,
        paddingLeft: "33px",
        marginLeft: "-33px",
        paddingRight: "32px",
        marginRight: "-32px",
        backgroundColor: "#F5F5F5",
        color: `rgba(51,51,51,${typeof value === "number" ? 1 - value / 100 : 1})`,
    },
    "& .MuiSlider-track": {
        border: "none",
        backgroundColor: "#F5F5F5",
        borderRadius: 28,
        paddingLeft: "28px",
        marginLeft: "-28px",
        paddingRight: "24px",
        marginRight: "-32px",
        height: 56,
    },
    "& .MuiSlider-thumb": {
        width: 56,
        height: 56,
        borderRadius: 28,
        color: disabled ? "#999" : "#0093D1",
        pointerEvents: "all",
        "&:before": {
            boxShadow: "none",
        },
        "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
            boxShadow: "inherit",
        },
    },
}));

interface ComponentProps extends React.HTMLAttributes<unknown> {}

const RailComponent = (props: ComponentProps) => {
    const { t } = useTranslation();
    const { children, ...other } = props;
    return (
        <SliderRail {...other}>
            {children}
            <Box
                sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: "4.5vw",
                        color: "inherit",
                        marginLeft: "20px",
                    }}>
                    {t("SummaryScreen.Extend irrevocably")}
                </Typography>
            </Box>
        </SliderRail>
    );
};

const ThumbComponent = (props: ComponentProps) => {
    const { children, ...other } = props;
    return (
        <SliderThumb {...other}>
            {children}
            <ArrowForwardIcon sx={{ color: "#FFFFFF" }} />
        </SliderThumb>
    );
};

const LoadingThumbComponent = (props: ComponentProps) => {
    const { children, ...other } = props;
    return (
        <SliderThumb {...other}>
            {children}
            <CircularProgress
                size={24}
                sx={{
                    color: "white",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                }}
            />
        </SliderThumb>
    );
};

const SwipeButton = (props: { confirmAction: () => void; disableGoForward: boolean; loadingState: LoadingState }) => {
    const { confirmAction, disableGoForward, loadingState } = props;
    const [value, setValue] = useState<number>(0);

    useEffect(() => {
        if (loadingState === LoadingState.Failed) {
            setValue(0);
        }
    }, [loadingState]);

    function handleChange(event: Event, newValue: number | number[]) {
        if (typeof newValue === "number" && loadingState !== LoadingState.Loading) {
            setValue(newValue);
        }
    }

    const handleChangeCommitted = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
        if (loadingState === LoadingState.Loading) {
            return;
        }

        if (newValue > 90) {
            confirmAction();
        } else {
            setValue(0);
        }
    };

    return (
        <Box sx={{ display: "flex", width: "80%", marginBottom: "4px" }}>
            <SwipeButtonSlider
                min={0}
                max={100}
                value={value}
                onChange={handleChange}
                onChangeCommitted={handleChangeCommitted}
                disabled={disableGoForward}
                components={{
                    Thumb: loadingState === LoadingState.Loading ? LoadingThumbComponent : ThumbComponent,
                    Rail: RailComponent,
                }}
            />
        </Box>
    );
};

export default SwipeButton;
